import { z } from 'zod';

export const zodParsingCaseStatusSchema = z.enum([
  'OPEN',
  'IN_PROGRESS',
  'NEEDS_ATTENTION',
  'RESOLVED',
  'DISMISSED',
  'DEBATCHED',
  'ARCHIVED',
]);

export const zodParsingCaseFailureCategorySchema = z.enum([
  'NONE',
  'PARSING_ERROR',
  'OCR_VALIDATION_ERROR',
  'OCR_ENGINE_ERROR',
  'PROCESSING_ERROR',
  'SCHEDULING_ERROR',
  'CONFIGURATION_ERROR',
  'POTENTIAL_DUPLICATE_DETECTED',
  'DATA_QUALITY_ERROR',
  'OTHER',
]);

const zodNamedEntitySchema = z.object({
  id: z.uuid(),
  name: z.string().nullish(),
});

const zodRelatedCaseSchema = z.object({
  createdOn: z.iso.datetime({ offset: true }).nullish(),
  deliveryNoteDate: z.string().nullish(),
  deliveryNoteId: z.string().nullish(),
  failureCategory: zodParsingCaseFailureCategorySchema.catch('OTHER'),
  finalAssetId: z.uuid().nullish(),
  id: z.uuid(),
  isSubsumed: z.boolean().default(false),
  pdfSasUrl: z.string().nullish(),
  primaryIdentifier: z.string().nullish(),
  status: zodParsingCaseStatusSchema,
});

/**
 * Shared base schema for a parsing case row.
 * Mirrors the BE `ParsingCaseListItem` (which is also the base for `ParsingCaseDetailResponse`).
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases
 */
export const zodParsingCaseBaseSchema = z.object({
  accountingReference: zodNamedEntitySchema.nullish(),
  accountingReferenceId: z.uuid().nullish(),
  attemptCount: z
    .number()
    .int()
    .nullish()
    .transform((value) => value ?? 0),
  buyerCompanyId: z.uuid().nullish(),
  buyerName: z.string().nullish(),
  createdOn: z.iso.datetime({ offset: true }).nullish(),
  deliveryNoteDate: z.string().nullish(),
  deliveryNoteId: z.string().nullish(),
  dismissedBy: z.uuid().nullish(),
  dismissedOn: z.iso.datetime({ offset: true }).nullish(),
  failureCategory: zodParsingCaseFailureCategorySchema.catch('OTHER'),
  failureDetails: z.record(z.string(), z.unknown()).nullish(),
  failureReason: z.string().nullish(),
  finalAssetId: z.uuid().nullish(),
  id: z.uuid(),
  isSubsumed: z.boolean().default(false),
  issuer: zodNamedEntitySchema.nullish(),
  issuerId: z.string().nullish(),
  jobId: z.uuid().nullish(),
  jobStatus: z.string().nullish(),
  latestTrigger: z.string().nullish(),
  missingParties: z.array(z.string()).nullish(),
  modifiedOn: z.iso.datetime({ offset: true }).nullish(),
  note: z.string().nullish(),
  ocrApiJobId: z.string().nullish(),
  pdfSasUrl: z.string().nullish(),
  primaryIdentifier: z.string().nullish(),
  relatedCases: z.array(zodRelatedCaseSchema).default([]),
  resolvedOn: z.iso.datetime({ offset: true }).nullish(),
  sellerCompanyId: z.uuid().nullish(),
  sellerName: z.string().nullish(),
  shipFromCompanyId: z.uuid().nullish(),
  shipToCompanyId: z.uuid().nullish(),
  site: zodNamedEntitySchema.nullish(),
  siteId: z.uuid().nullish(),
  status: zodParsingCaseStatusSchema,
  subsumedByCaseId: z.uuid().nullish(),
  technicalIssuerId: z.uuid().nullish(),
});

/**
 * Schema for the response after an action (retry / approve) creates a new job on a case.
 */
export const zodParsingCaseActionResponseSchema = z.object({
  caseId: z.uuid(),
  jobId: z.uuid(),
  parsedJsonSasUrl: z.string().nullish(),
  status: zodParsingCaseStatusSchema,
});

/**
 * Schema for the response after dismissing a parsing case.
 */
export const zodDismissParsingCaseResponseSchema = z.object({
  caseId: z.uuid(),
  dismissedOn: z.iso.datetime({ offset: true }),
  status: zodParsingCaseStatusSchema,
});

/**
 * Schema for the response after saving a parsing case note.
 */
export const zodParsingCaseNoteResponseSchema = z.object({
  caseId: z.uuid(),
  note: z.string().nullable(),
});

/**
 * Schema for the response after saving a parsing case draft.
 */
export const zodParsingCaseDraftResponseSchema = z.object({
  caseId: z.uuid(),
  draft: z.json().nullable(),
});
